// Left sidebar Search and Tags panes.
import { clear, debounce, h, snippetHtml } from './dom.js';
import type { SearchHit, TagCount } from './types.js';
import type { App } from './app.js';

/** Search pane: full-text query against `/api/search`. */
export class SearchPane {
  readonly el: HTMLElement;
  private readonly app: App;
  private readonly input: HTMLInputElement;
  private readonly results: HTMLElement;
  private readonly summary: HTMLElement;
  private lastHits: SearchHit[] = [];

  constructor(app: App) {
    this.app = app;
    this.input = h('input', {
      class: 'text-input search-input',
      type: 'search',
      placeholder: 'Search the vault…',
      attrs: { 'aria-label': 'Search the vault' },
    });
    this.results = h('div', { class: 'result-list' });
    this.summary = h('div', { class: 'pane-summary' });
    const run = debounce(() => void this.run(), 220);
    this.input.addEventListener('input', run);
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') void this.run();
    });
    this.el = h('div', { class: 'search-pane' }, [this.input, this.summary, this.results]);
  }

  get query(): string {
    return this.input.value;
  }

  focus(): void {
    this.input.focus();
    this.input.select();
  }

  /** Sets the query text; `run` executes the search immediately. */
  setQuery(text: string, run = true): void {
    this.input.value = text;
    if (run) void this.run();
  }

  async run(): Promise<void> {
    const raw = this.input.value.trim();
    // `#tag` is a tag link, but FTS treats `#` as a separator: search the bare token.
    const query = raw.replace(/(^|\s)#/g, '$1');
    if (!query) {
      this.lastHits = [];
      clear(this.results);
      this.summary.textContent = '';
      return;
    }
    try {
      const response = await this.app.api.search(query);
      this.lastHits = response.results;
      this.render(raw);
    } catch (error) {
      this.summary.textContent = `Search failed: ${(error as Error).message}`;
    }
  }

  private render(raw: string): void {
    clear(this.results);
    this.summary.textContent = this.lastHits.length === 0 ? `No notes match “${raw}”` : `${this.lastHits.length} match(es)`;
    for (const hit of this.lastHits) {
      const row = h('button', { class: 'result-row', type: 'button' }, [
        h('span', { class: 'result-title', text: hit.title || hit.path }),
        h('span', { class: 'result-path', text: hit.path }),
        h('span', { class: 'result-snippet', html: snippetHtml(hit.snippet) }),
      ]);
      row.addEventListener('click', () => void this.app.open(hit.path));
      this.results.append(row);
    }
  }

  /** Refreshes the current query (used after the vault changed). */
  refreshIfActive(): void {
    if (this.input.value.trim()) void this.run();
  }
}

/** Tags pane: tag cloud with counts; clicking a tag runs a search for it. */
export class TagsPane {
  readonly el: HTMLElement;
  private readonly container: HTMLElement;
  private readonly app: App;

  constructor(app: App) {
    this.app = app;
    this.container = h('div', { class: 'tag-list' });
    this.el = h('div', { class: 'tags-pane' }, [this.container]);
  }

  setTags(tags: TagCount[]): void {
    clear(this.container);
    if (tags.length === 0) {
      this.container.append(h('p', { class: 'empty-note', text: 'No tags found in this vault.' }));
      return;
    }
    for (const tag of tags) {
      const chip = h('button', {
        class: 'tag-chip',
        type: 'button',
        title: `${tag.count} note(s) — search for #${tag.tag}`,
      }, [
        h('span', { class: 'tag-name', text: `#${tag.tag}` }),
        h('span', { class: 'tag-count', text: String(tag.count) }),
      ]);
      chip.addEventListener('click', () => this.app.runSearch(`#${tag.tag}`));
      this.container.append(chip);
    }
  }
}
